import { Link } from "react-router-dom";
import {
  GraduationCap, Users, ShieldCheck, Compass, BadgeCheck, Phone,
  Plane, Bus, UtensilsCrossed, ClipboardCheck, Headset, Sparkles
} from "lucide-react";

const pillars = [
  { icon: GraduationCap, t: "Educator-first design", d: "Itineraries align with classroom goals, college access objectives, and program outcomes. We brief chaperones, not just drivers." },
  { icon: Users, t: "Built for 25+ groups", d: "Group block lodging, chartered transport, group dining — every line item is priced for groups of twenty-five or more." },
  { icon: ShieldCheck, t: "On-trip support", d: "A dedicated KNV tour manager travels with your group. Our home team is on-call 24/7 for the entire trip." },
  { icon: Compass, t: "Custom, never canned", d: "Every tour comes with a dedicated tour coordinator who plans and customizes the trip with you in advance." },
  { icon: BadgeCheck, t: "Vetted partners", d: "Hotels, motorcoaches, and on-the-ground guides all on our preferred-vendor list. No surprises mid-trip." },
  { icon: Phone, t: "Real humans", d: "No booking engine on purpose. The person who designs your trip is the person you'll talk to on the phone." },
];

const inclusives = [
  { icon: ClipboardCheck, t: "Dedicated tour coordinator", d: "Plans and customizes the trip with you before departure." },
  { icon: Headset, t: "On-trip tour manager", d: "Travels with your group and handles every logistic on the ground." },
  { icon: Plane, t: "Round-trip air", d: "Group air arrangements negotiated and ticketed by KNV." },
  { icon: Bus, t: "Round-trip ground transport", d: "Chartered motorcoach and all airport transfers included." },
  { icon: UtensilsCrossed, t: "All meals", d: "Breakfast, lunch and dinner — with dietary accommodations." },
  { icon: Sparkles, t: "All activities & entries", d: "Every museum, campus visit, and excursion is paid for." },
];

export default function Why() {
  return (
    <>
      <section className="container-edge py-20">
        <div className="eyebrow mb-3"><Sparkles size={14} className="fill-coral" /> Why KNV Travelz</div>
        <h1 className="display-serif text-5xl sm:text-6xl leading-[0.98] max-w-3xl">
          The reasons educators and group leaders <span className="hand-accent text-coral">come back</span>.
        </h1>
      </section>

      <section className="container-edge pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map(({ icon: Icon, t, d }, i) => (
            <div key={t} className={`bg-white rounded-3xl p-8 ring-1 ring-ink/8 hover:soft-shadow hover:-translate-y-1 transition-all ${i % 3 === 1 ? "md:translate-y-6" : ""}`}>
              <div className="w-14 h-14 rounded-2xl bg-coral/10 flex items-center justify-center mb-5">
                <Icon size={28} strokeWidth={1.75} className="text-coral" />
              </div>
              <h3 className="display-serif text-2xl mb-3">{t}</h3>
              <p className="text-muted2 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-teal/8 py-20">
        <div className="container-edge">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3">The All-Inclusive Promise</div>
            <h2 className="display-serif text-4xl sm:text-5xl leading-tight">
              Every KNV tour includes these. <span className="hand-accent text-coral">Always.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {inclusives.map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-white rounded-3xl p-7 ring-1 ring-ink/8">
                <Icon size={26} strokeWidth={1.5} className="text-coral mb-4" />
                <h3 className="display-serif text-xl mb-2">{t}</h3>
                <p className="text-muted2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-edge py-20">
        <div className="bg-ink text-cream rounded-[2.5rem] p-12 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-coral/25 rounded-full blur-3xl" />
          <div className="relative">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-sun mb-5">No bookings online</div>
            <h2 className="display-serif text-4xl sm:text-5xl text-cream leading-tight mb-5">
              Every trip starts with a <span className="hand-accent text-sun">conversation</span>.
            </h2>
            <p className="text-cream/80 mb-8 max-w-xl mx-auto">Tell us about your group — we'll reach out in two business days.</p>
            <Link to="/request-quote" className="btn-primary">Plan My Trip</Link>
          </div>
        </div>
      </section>
    </>
  );
}
